import { useEffect, useMemo, useState } from "react";
import { Center, Loader, Flex, Badge, useMantineTheme, Paper, Text, Stack, Group } from "@mantine/core";
import { MantineReactTable, type MRT_ColumnDef } from "mantine-react-table";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";

interface CollaborationLogHistoryTabProps {
  matchId: string;
}

type LogItem = {
  id: string;
  createdAt: string;
  status: string;
  requesterCompanyName: string | null;
  receiverCompanyName: string | null;
  paymentMethods: {
    requester: string;
    receiver: string;
  } | null;
};

type QuestionnaireForMatch = {
  questionnaireAvailable: true;
  bothPaidAt: string | null;
  rowExists: boolean;
  questionnaireId: string | null;
  requester: { status: "submitted" | "not_submitted"; submittedAt: string | null };
  receiver: { status: "submitted" | "not_submitted"; submittedAt: string | null };
  grading: "none" | "pending" | "done";
  gradingUpdatedAt: string | null;
  nda?: {
    recordExists: boolean;
    requesterSigned: boolean;
    receiverSigned: boolean;
  };
};

const STATUS_LABELS: Record<string, string> = {
  REQUESTED: "Interest Request Sent",
  RECEIVER_DECLINED: "Request Declined",
  BOTH_ACCEPTED: "Request Accepted",
  REQUESTER_PAID_ONLY: "Requester Paid",
  RECEIVER_PAID_ONLY: "Requestee Paid",
  BOTH_PAID: "Collaboration Active",
  PAYMENT_EXPIRED: "Payment Expired",
};

const STATUS_COLORS: Record<string, string> = {
  REQUESTED: "blue",
  RECEIVER_DECLINED: "red",
  BOTH_ACCEPTED: "cyan",
  REQUESTER_PAID_ONLY: "yellow",
  RECEIVER_PAID_ONLY: "yellow",
  BOTH_PAID: "green",
  PAYMENT_EXPIRED: "gray",
};

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  RAZORPAY: "Razorpay",
  WALLET: "Wallet",
};

const PAYMENT_METHOD_COLORS: Record<string, string> = {
  RAZORPAY: "blue",
  WALLET: "green",
};

export default function CollaborationLogHistoryTab({ matchId }: CollaborationLogHistoryTabProps) {
  const theme = useMantineTheme();

  const [logs, setLogs] = useState<LogItem[]>([]);
  const [questionnaireForMatch, setQuestionnaireForMatch] = useState<QuestionnaireForMatch | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLogs = async () => {
      setLoading(true);
      try {
        const [raw, details] = await Promise.all([
          trpc.admin.getCollaborationLogs.query({ matchId }),
          trpc.admin
            .getCollaborationDetails.query({ matchId, includeProcessTimeline: true } as {
              matchId: string;
              includeProcessTimeline?: boolean;
            })
            .catch(() => null),
        ]);

        const res = Array.isArray(raw)
          ? { logs: raw as any[], questionnaireForMatch: null as QuestionnaireForMatch | null }
          : (raw as { logs: any[]; questionnaireForMatch: QuestionnaireForMatch | null });

        const fromDetails =
          (details as { processTimeline?: { questionnaireSnapshot?: QuestionnaireForMatch | null } } | null)
            ?.processTimeline?.questionnaireSnapshot ?? null;

        // Sort logs by createdAt ascending to show chronological flow (oldest first)
        const sortedLogs = (res.logs ?? [])
          .map((log: any) => ({
            id: log.id,
            createdAt: log.createdAt,
            status: log.status,
            requesterCompanyName: log.requester?.legalName || null,
            receiverCompanyName: log.receiver?.legalName || null,
            paymentMethods: log.paymentMethods,
          }))
          .sort((a: LogItem, b: LogItem) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

        setLogs(sortedLogs);
        setQuestionnaireForMatch(res.questionnaireForMatch ?? fromDetails ?? null);
      } catch (err) {
        handleTrpcError(err);
        setLogs([]);
        setQuestionnaireForMatch(null);
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [matchId]);

  const columns: MRT_ColumnDef<LogItem>[] = useMemo(
    () => [
      {
        accessorKey: "createdAt",
        header: "Date",
        size: 180,
        Cell: ({ cell }) => {
          const value = cell.getValue() as string;
          if (!value) return <span style={{ color: "#94a3b8" }}>—</span>;
          return new Date(value).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          });
        },
      },
      {
        accessorKey: "requesterCompanyName",
        header: "Requester Company",
        size: 200,
        Cell: ({ cell }) => {
          const value = cell.getValue() as string | null;
          return value || "—";
        },
      },
      {
        accessorKey: "receiverCompanyName",
        header: "Requestee Company",
        size: 200,
        Cell: ({ cell }) => {
          const value = cell.getValue() as string | null;
          return value || "—";
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 180,
        Cell: ({ cell }) => {
          const value = cell.getValue() as string;
          const color = STATUS_COLORS[value] || "gray";
          const label = STATUS_LABELS[value] || value;
          return (
            <Badge variant="light" color={color} size="sm">
              {label}
            </Badge>
          );
        },
      },
      {
        accessorKey: "paymentMethods",
        header: "Payment Method",
        size: 200,
        Cell: ({ cell, row }) => {
          const paymentMethods = cell.getValue() as LogItem["paymentMethods"];
          const status = row.original.status;

          // Only show payment methods for BOTH_PAID status
          if (status !== "BOTH_PAID" || !paymentMethods) {
            return <span style={{ color: "#94a3b8" }}>—</span>;
          }

          return (
            <Flex gap="xs" wrap="wrap">
              <Badge variant="light" color={PAYMENT_METHOD_COLORS[paymentMethods.requester] || "gray"} size="sm">
                R: {PAYMENT_METHOD_LABELS[paymentMethods.requester] || paymentMethods.requester}
              </Badge>
              <Badge variant="light" color={PAYMENT_METHOD_COLORS[paymentMethods.receiver] || "gray"} size="sm">
                E: {PAYMENT_METHOD_LABELS[paymentMethods.receiver] || paymentMethods.receiver}
              </Badge>
            </Flex>
          );
        },
      },
    ],
    []
  );

  if (loading) {
    return (
      <Center h="300px">
        <Loader size="lg" variant="dots" />
      </Center>
    );
  }

  const sideBadge = (label: string, status: "submitted" | "not_submitted", at: string | null) => (
    <Group gap="xs" wrap="nowrap">
      <Text size="sm" fw={500}>
        {label}
      </Text>
      <Badge size="sm" variant="light" color={status === "submitted" ? "green" : "orange"}>
        {status === "submitted" ? "Completed" : "Not submitted (pending)"}
      </Badge>
      {at ? (
        <Text size="xs" c="dimmed">
          {new Date(at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}
        </Text>
      ) : null}
    </Group>
  );

  const gradingLabel =
    questionnaireForMatch?.grading === "done"
      ? { text: "Grading saved", color: "green" as const }
      : questionnaireForMatch?.grading === "pending"
        ? { text: "Awaiting grading", color: "yellow" as const }
        : { text: "Grading not applicable yet", color: "gray" as const };

  return (
    <Flex direction="column" w="100%" gap="md">
      {questionnaireForMatch ? (
        <Paper p="md" radius="md" withBorder>
          <Text size="sm" fw={600} mb="xs">
            Questionnaire & NDA (current state)
          </Text>
          <Stack gap="sm">
            {sideBadge("Requester", questionnaireForMatch.requester.status, questionnaireForMatch.requester.submittedAt)}
            {sideBadge("Requestee", questionnaireForMatch.receiver.status, questionnaireForMatch.receiver.submittedAt)}
            <Group gap="xs">
              <Text size="sm" fw={500}>
                Grading
              </Text>
              <Badge size="sm" variant="light" color={gradingLabel.color}>
                {gradingLabel.text}
              </Badge>
              {questionnaireForMatch.grading === "done" && questionnaireForMatch.gradingUpdatedAt ? (
                <Text size="xs" c="dimmed">
                  Updated {new Date(questionnaireForMatch.gradingUpdatedAt).toLocaleString(undefined, {
                    dateStyle: "short",
                    timeStyle: "short",
                  })}
                </Text>
              ) : null}
            </Group>
            {!questionnaireForMatch.rowExists ? (
              <Text size="xs" c="dimmed">
                No questionnaire answers have been saved for this match yet.
              </Text>
            ) : null}

            {questionnaireForMatch.nda ? (
              <>
                <Text size="sm" fw={600} mt="sm">
                  NDA
                </Text>
                <Group gap="xs" wrap="wrap">
                  <Text size="sm" fw={500}>
                    NDA record
                  </Text>
                  <Badge size="sm" variant="light" color={questionnaireForMatch.nda.recordExists ? "blue" : "gray"}>
                    {questionnaireForMatch.nda.recordExists ? "On file" : "Not created yet"}
                  </Badge>
                </Group>
                <Group gap="xs" wrap="wrap">
                  <Text size="sm" fw={500}>
                    Requester signed
                  </Text>
                  <Badge size="sm" variant="light" color={questionnaireForMatch.nda.requesterSigned ? "green" : "orange"}>
                    {questionnaireForMatch.nda.requesterSigned ? "Yes" : "No"}
                  </Badge>
                </Group>
                <Group gap="xs" wrap="wrap">
                  <Text size="sm" fw={500}>
                    Requestee signed
                  </Text>
                  <Badge size="sm" variant="light" color={questionnaireForMatch.nda.receiverSigned ? "green" : "orange"}>
                    {questionnaireForMatch.nda.receiverSigned ? "Yes" : "No"}
                  </Badge>
                </Group>
              </>
            ) : null}
          </Stack>
        </Paper>
      ) : (
        <Text size="xs" c="dimmed">
          After both companies pay, questionnaire and NDA status load here. If this stays empty, refresh the page or
          restart the API server so it runs the latest admin endpoints.
        </Text>
      )}

      <MantineReactTable
        columns={columns}
        data={logs}
        enableRowNumbers
        enableStickyHeader
        enableColumnPinning
        enableColumnResizing
        columnResizeMode="onEnd"
        layoutMode="semantic"
        enableGlobalFilter={false}
        enableRowActions={false}
        enableSorting={false}
        enableColumnFilters={false}
        initialState={{
          density: "md",
        }}
        mantineTableBodyCellProps={{
          style: {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: 200,
          },
        }}
        mantineTableHeadCellProps={{
          style: {
            fontWeight: 500,
            fontSize: "var(--mantine-font-size-md)",
            color: theme.colors.slate[6],
          },
        }}
        mantineTableContainerProps={{
          style: {
            maxHeight: "400px",
            minHeight: "100px",
            width: "100%",
            overflowX: "auto",
          },
        }}
        renderEmptyRowsFallback={() => (
          <tr>
            <td colSpan={columns.length + 1} style={{ textAlign: "center", padding: "2rem" }}>
              No activity logs found for this collaboration.
            </td>
          </tr>
        )}
      />
    </Flex>
  );
}
